import { useState, useRef } from 'react';
import { Search, Download, Plus, Upload, Edit, Trash, Heart, CreditCard, AlertCircle } from 'lucide-react';
import Table from '../components/Table.tsx';
import { useContributions, useExportContributions, useCreateContribution, useUpdateContribution, useDeleteContribution, useContributionStats, useGetContributionTemplate, useBulkUploadContribution } from '../hooks/useContributions.ts';
import { useDebounce } from '../hooks/useDebounce.ts';
import ContributionModal from '../components/ContributionModal.tsx';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';
import Select from '../components/ui/Select.tsx';
import AsyncMemberSelect from '../components/ui/AsyncMemberSelect.tsx';
import PhoneFilters from '../components/PhoneFilters';
import { useNavigate } from 'react-router-dom';
import { useSubgroups } from '../hooks/useSubgroups.ts';
import { useQuery } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../utils/axios.ts';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

export default function ContributionsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [memberFilter, setMemberFilter] = useState(null);
  const [subgroupFilter, setSubgroupFilter] = useState(null);
  const [villageFilter, setVillageFilter] = useState(null);
  const [welfareFilter] = useState(null);
  const [driveFilter, setDriveFilter] = useState(null);
  const [singleDate, setSingleDate] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 500);

  useIsAdmin();
  const { data: profile } = useGetProfile();
  
  const isHouseManagement = Number(profile?.data?.current_group?.is_house_management) === 1;
  const isIrregular = Number(profile?.data?.current_group?.is_irregular) === 1;
  const houseLabel = profile?.data?.current_group?.house_label || 'House';

  const formatLocalDate = (date) =>
    date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : undefined;

  const { data: contributionsData, isLoading } = useContributions({
    search: debouncedSearch,
    sort_field: sortField,
    sort_direction: sortDirection,
    page,
    per_page: 10,
    member_id: memberFilter?.value,
    subgroup_id: subgroupFilter?.value,
    welfare_plan_id: welfareFilter?.value,
    drive_id: driveFilter?.value,
    date: formatLocalDate(singleDate),
    date_from: formatLocalDate(startDate),
    date_to: formatLocalDate(endDate),
  });

  const { data: contributionStats } = useContributionStats({
    search: debouncedSearch,
    sort_field: sortField,
    sort_direction: sortDirection,
    page,
    per_page: 10,
    member_id: memberFilter?.value,
    subgroup_id: subgroupFilter?.value,
    welfare_plan_id: welfareFilter?.value,
    drive_id: driveFilter?.value,
    date: formatLocalDate(singleDate),
    date_from: formatLocalDate(startDate),
    date_to: formatLocalDate(endDate),
  });

  const exportContributions = useExportContributions();
  const createContribution = useCreateContribution();
  const updateContribution = useUpdateContribution();
  const deleteContribution = useDeleteContribution();
  const contributionTemplate = useGetContributionTemplate();
  const bulkUploadContributions = useBulkUploadContribution();

  const fileInputRef = useRef(null);

  const { data: subgroupsData } = useSubgroups();
  const rawSubgroups = subgroupsData?.data || [];
  const subgroupOptions = rawSubgroups.filter(s => s.type === 'subgroup' || !s.type).map(s => ({ value: s.id, label: s.name }));
  const villageOptions = rawSubgroups.filter(s => s.type === 'village').map(s => ({ value: s.id, label: s.name }));

  const { data: welfarePlansData } = useQuery({
    queryKey: ['welfare-plans'],
    queryFn: async () => {
      const res = await api.get('/welfare-plans');
      return res.data;
    },
  });

  // Fetch contribution drives for filter
  const { data: contributionDrivesData, isLoading: isDrivesLoading, error: drivesError } = useQuery({
    queryKey: ['contribution-drives'],
    queryFn: async () => {
      const { data } = await api.get('/contribution-drives');
      return data;
    },
  });
  
  // Debug contribution drives data
  console.log('🔍 Contribution Drives Debug:', {
    contributionDrivesData,
    isDrivesLoading,
    drivesError,
    isArray: Array.isArray(contributionDrivesData),
    dataLength: contributionDrivesData?.length
  });
  
  const driveOptions = Array.isArray(contributionDrivesData) 
    ? contributionDrivesData.map(drive => ({ 
        value: drive.id, 
        label: drive.title 
      })) 
    : [];
  
  console.log('🔍 Drive Options:', driveOptions);

  // Calculate totals from the API data
  const totalAmount = contributionStats?.data?.sum || 0;
  
  const columns = [
    {
      label: 'Member',
      field: 'member',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => row.member?.name || '-',
    },
    {
      label: 'Phone',
      field: 'member',
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => row.member?.phone_number || '-',
    },
    ...(isHouseManagement ? [{
      label: houseLabel,
      field: 'subgroup',
      sortable: false,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => row.member?.subgroup?.name || '-',
    }] : []),
    {
      label: 'Amount',
      field: 'amount',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.amount > 0 ? `Ksh ${Number(row.amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '-'}
          {row.welfare_plan_id && row.welfare_plan_name && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 text-xs font-semibold ml-2 cursor-pointer hover:bg-pink-200 transition"
              title={`Welfare: ${row.welfare_plan_name}`}
            >
              <Heart size={14} className="mr-1 text-pink-500" />
              {row.welfare_plan_name}
            </span>
          )}
        </div>
      ),
    },
    {
      label: 'Drive/Category',
      field: 'drive',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => {
        if (row.drive_name) {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
              {row.drive_name}
            </span>
          );
        }
        return '-';
      },
    },
    {
      label: 'Date',
      field: 'contribution_date',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => row.contribution_date || '-',
    },
    {
      label: 'Notes',
      field: 'notes',
      sortable: true,
      className: 'hidden sm:table-cell px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => row.notes || '-',
    },
    {
      label: 'Created At',
      field: 'created_at',
      sortable: true,
      className: 'px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => row.created_at || '-',
    },
    {
      label: 'Actions',
      field: 'actions',
      className: 'text-end px-2 py-2 text-xs md:px-4 md:py-3 md:text-sm',
      render: (row) => (
        <div className="flex space-x-2">
          {can(PERMISSIONS.EDIT_CONTRIBUTION) && (
          <button 
            className="text-blue-600 hover:text-blue-800"
            onClick={() => {
              setSelectedContribution(row);
              setIsModalOpen(true);
            }}
          >
            <Edit size={18} />
          </button>
          )}
          {can(PERMISSIONS.DELETE_CONTRIBUTION) && (
          <button 
            className="text-red-600 hover:text-red-800"
            onClick={() => handleDeleteContribution(row.id)}
          >
            <Trash size={18} />
          </button>
          )}
        </div>
      )
    },
  ];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = (type) => {
    exportContributions.mutate(
      {
        type,
        search: debouncedSearch,
        sort_field: sortField,
        sort_direction: sortDirection,
        member_id: memberFilter?.value,
        subgroup_id: subgroupFilter?.value,
        welfare_plan_id: welfareFilter?.value,
        drive_id: driveFilter?.value,
        date: formatLocalDate(singleDate),
        date_from: formatLocalDate(startDate),
        date_to: formatLocalDate(endDate),
      },
      {
        onSuccess: (data) => {
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');
          link.href = url;
          const ext = type === 'pdf' ? 'pdf' : (type === 'csv' ? 'csv' : 'xlsx');
          link.setAttribute('download', `contributions.${ext}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success(`Contributions exported as ${type.toUpperCase()}`);
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error) || 'Export failed');
        }
      }
    );
  };

  const handleMatrixExport = async () => {
    try {
      const params = {};
      if (subgroupFilter?.value) params.subgroup_id = subgroupFilter.value;
      if (villageFilter?.value) params.village_id = villageFilter.value;
      if (searchQuery) params.search = searchQuery;
      const res = await api.get('/contributions/matrix-export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contributions_matrix_breakdown.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Matrix Breakdown exported successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to export matrix breakdown');
    }
  };

  const handleCreateContribution = (data) => {
    createContribution.mutate(data, {
      onSuccess: () => {
        setIsModalOpen(false);
        setSelectedContribution(null);
      },
    });
  };

  const handleUpdateContribution = (data) => {
    updateContribution.mutate(
      { id: selectedContribution.id, ...data },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedContribution(null);
        },
      }
    );
  };

  const handleDeleteContribution = (id) => {
    if (window.confirm('Are you sure you want to delete this contribution?')) {
      deleteContribution.mutate(id);
    }
  };


  const handleGenerateTemplate = () => {
    contributionTemplate.mutate();
    
  };


  const handleImport = (file) => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    bulkUploadContributions.mutate(file,
      {
       onSuccess: () => {
          toast.success(`Contributions uploaded as successfully`);
          setShowImportModal(false);
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error) || 'Failed to upload contributions');
        }
      }
    );
    
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Contributions</h1>
        <div className="flex space-x-3">
          {can(PERMISSIONS.EXPORT_CONTRIBUTIONS) && (
          <>
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <Download size={20} className="mr-2" />
            Export
          </button>
          <button
            onClick={handleMatrixExport}
            className="bg-emerald-700 text-white px-4 py-2 rounded-lg hover:bg-emerald-800 flex items-center shadow-sm"
            title="Export Drive Matrix Breakdown with Subgroups & Villages"
          >
            <Download size={20} className="mr-2" />
            Matrix Export
          </button>
          </>
          )}
          {/* Pay via M-Pesa Button */}
          <div className="relative group">
            <button
              className={`flex items-center px-4 py-2 rounded-lg ${
                profile?.data?.mpesa_credentials 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-400 text-white cursor-not-allowed'
              }`}
              onClick={() => {
                if (profile?.data?.mpesa_credentials) {
                  navigate('/pay-contribution');
                }
              }}
              disabled={!profile?.data?.mpesa_credentials}
            >
              <CreditCard size={20} className="mr-2" />
              Pay via M-Pesa
            </button>

            {/* Tooltip */}
            {!profile?.data?.mpesa_credentials && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                <div className="flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  M-Pesa integration not configured
                </div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            )}
          </div>

          {can(PERMISSIONS.CREATE_CONTRIBUTION) && (
          <>
          <button
            onClick={() => setShowImportModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
          >
            <Upload size={20} className="mr-2" />
            Import
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus size={20} className="mr-2" />
            Add Contribution
          </button>
          </>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="flex justify-start mb-6">
        <div className="bg-white rounded-xl shadow p-6 w-80">
          <p className="text-sm text-gray-500 mb-1">Total Contributions</p>
          <h3 className="text-xl font-bold">Ksh {Number(totalAmount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</h3>
          <div className="mt-2 text-sm text-gray-500">
            From {contributionStats?.data?.count || 0} entries
          </div>
          {contributionStats?.data?.rent_sum !== undefined && (
            <div className="mt-2 text-xs text-gray-500">
              Service charge: Ksh {Number(contributionStats.data.rent_sum || 0).toLocaleString()} · Drives (separate): Ksh {Number(contributionStats.data.drive_sum || 0).toLocaleString()}
            </div>
          )}
        </div>
      </div>
      
      {/* Filter Section */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search (notes, amount, etc)</label>
            <div className="relative">
              <input 
                type="text"
                placeholder="Search by notes, amount, etc..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>
          <PhoneFilters count={[memberFilter, subgroupFilter, villageFilter, driveFilter, singleDate, startDate].filter(Boolean).length}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
            <AsyncMemberSelect
              value={memberFilter}
              onChange={setMemberFilter}
              isClearable
              placeholder="Filter by member..."
              classNamePrefix="select"
            />
          </div>
          {(isHouseManagement || isIrregular) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subgroup / Category</label>
              <Select
                options={subgroupOptions}
                value={subgroupFilter}
                onChange={setSubgroupFilter}
                isClearable
                placeholder="Filter by subgroup / category..."
                classNamePrefix="select"
              />
            </div>
          )}
          {isIrregular && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Village / Region</label>
              <Select
                options={villageOptions}
                value={villageFilter}
                onChange={setVillageFilter}
                isClearable
                placeholder="Filter by village / region..."
                classNamePrefix="select"
              />
            </div>
          )}
          {/*<div>*/}
          {/*  <label className="block text-sm font-medium text-gray-700 mb-1">Welfare Plan</label>*/}
          {/*  <Select*/}
          {/*    options={welfareOptions}*/}
          {/*    value={welfareFilter}*/}
          {/*    onChange={setWelfareFilter}*/}
          {/*    isClearable*/}
          {/*    placeholder="Filter by welfare..."*/}
          {/*    classNamePrefix="select"*/}
          {/*  />*/}
          {/*</div>*/}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Drive/Category</label>
            <Select
              options={driveOptions}
              value={driveFilter}
              onChange={setDriveFilter}
              isClearable
              placeholder="Filter by drive/category..."
              classNamePrefix="select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Single Date</label>
            <DatePicker
              selected={singleDate}
              onChange={date => setSingleDate(date)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholderText="Pick a date..."
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={update => setDateRange(update)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholderText="Pick date range..."
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>
          </PhoneFilters>
        </div>
      </div>
      
      {/* Table Component */}
      <Table
        columns={columns}
        data={contributionsData?.data || []}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        page={page}
        perPage={10}
        total={contributionsData?.meta?.total || 0}
        onPageChange={setPage}
        loading={isLoading}
      />

      <ContributionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedContribution(null);
        }}
        onSubmit={selectedContribution ? handleUpdateContribution : handleCreateContribution}
        contribution={selectedContribution}
        isEditing={!!selectedContribution}
        isLoading={createContribution.isPending || updateContribution.isPending}
      />

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Export Format</h2>
            <p className="text-gray-600 mb-6">Choose the format for your contributions export:</p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={exportContributions.isPending}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-green-600 font-bold text-lg">X</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Excel (.xlsx)</div>
                    <div className="text-sm text-gray-500">Best for data analysis and calculations</div>
                  </div>
                </div>
                {exportContributions.isPending && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                )}
              </button>

              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={exportContributions.isPending}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">CSV</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">CSV (.csv)</div>
                    <div className="text-sm text-gray-500">Simple text format, compatible with most applications</div>
                  </div>
                </div>
                {exportContributions.isPending && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                )}
              </button>

              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={exportContributions.isPending}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-red-600 font-bold text-sm">PDF</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">PDF (.pdf)</div>
                    <div className="text-sm text-gray-500">Professional report format, perfect for sharing</div>
                  </div>
                </div>
                {exportContributions.isPending && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                )}
              </button>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                disabled={exportContributions.isPending}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Bulk upload contributions</h2>
            
            <div className="space-y-3">
              <button
                onClick={() => handleGenerateTemplate()}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-green-600 font-bold text-lg">Excel</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Get template</div>
                  </div>
                </div>
            
              </button>

              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                ref={fileInputRef}
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleImport(e.target.files[0]);
                  }
                }}
              />
               <button
          onClick={() => fileInputRef.current.click()}
          className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-blue-600 font-bold text-sm">⬆</span>
            </div>
            <div>
              <div className="font-medium text-gray-900">Upload template</div>
            </div>
          </div>
        </button>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                disabled={exportContributions.isPending}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}